/**
 * postmessage.ts — postMessage protocol v1.0 handler.
 *
 * Messages FROM the iframe (trusted origin only):
 *   { version:'1.0', type:'mp:ready' }
 *   { version:'1.0', type:'mp:step-change',      payload:{step,stepIndex,totalSteps,sessionId} }
 *   { version:'1.0', type:'mp:booking:complete',  payload:{appointmentId,sessionId} }
 *   { version:'1.0', type:'mp:booking:error',     payload:{message,sessionId} }
 *   { version:'1.0', type:'mp:close-request' }
 *   { version:'1.0', type:'mp:height-hint',       payload:{height} }
 *   { version:'1.0', type:'mp:scroll-request',    payload:{top} }
 *
 * Legacy v0 messages (no version field) are still accepted for backwards
 * compatibility during the transition period:
 *   { type:'mp:booking-complete', payload:{appointmentId} }
 *   { type:'mp:close' }
 *
 * For each booking event a matching DOM CustomEvent is dispatched on window
 * so customers can hook in GTM, FB Pixel, GA4, etc. without depending on the
 * ManagePatients.on() API:
 *   managepatients:ready
 *   managepatients:stepChange
 *   managepatients:bookingComplete
 *   managepatients:bookingError
 *
 * Security: we verify the message origin is our app (apex or www host of the
 * CDN) before acting — the apex 307-redirects to www, so iframes may end up
 * on either.
 */

declare const __MP_CDN__: string

export interface BookingCompletePayload {
  appointmentId: string | null
  sessionId?:    string | null
}

export interface BookingErrorPayload {
  message:    string
  sessionId?: string | null
}

export interface StepChangePayload {
  step:       string
  stepIndex:  number
  totalSteps: number
  sessionId?: string | null
}

interface MessageHandlers {
  onReady:           () => void
  onStepChange:      (payload: StepChangePayload) => void
  onBookingComplete: (payload: BookingCompletePayload) => void
  onBookingError:    (payload: BookingErrorPayload) => void
  onCloseRequest:    () => void
  /** Optional: iframe content height (px) — used by inline mode to auto-size. */
  onHeightHint?:     (height: number) => void
  /** When set, height hints are only accepted from this iframe's window. */
  source?:           HTMLIFrameElement
}

/** Origins we accept messages from: the CDN origin plus its www/apex twin. */
export function trustedOrigins(cdn: string): Set<string> {
  const u = new URL(cdn)
  const host = u.hostname.startsWith('www.') ? u.hostname.slice(4) : u.hostname
  const port = u.port ? `:${u.port}` : ''
  return new Set([
    u.origin,
    `${u.protocol}//${host}${port}`,
    `${u.protocol}//www.${host}${port}`,
  ])
}

/** Dispatch a CustomEvent on window for customer GTM/analytics hooks. */
function dispatchCustomEvent(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail, bubbles: false }))
  } catch { /* ignore — CustomEvent may not be available in very old browsers */ }
}

export function listenForMessages(handlers: MessageHandlers): () => void {
  const origins = trustedOrigins(__MP_CDN__)

  function handleMessage(e: MessageEvent): void {
    // Only accept messages from our own app (apex or www)
    if (!origins.has(e.origin)) return

    const data = e.data
    if (!data || typeof data !== 'object') return

    const isV1 = data.version === '1.0'

    switch (data.type) {
      // ── v1 events ────────────────────────────────────────────────────────
      case 'mp:ready': {
        handlers.onReady()
        dispatchCustomEvent('managepatients:ready', null)
        break
      }

      case 'mp:step-change': {
        const p: StepChangePayload = data.payload ?? {}
        handlers.onStepChange(p)
        dispatchCustomEvent('managepatients:stepChange', p)
        break
      }

      case 'mp:booking:complete': {
        const p: BookingCompletePayload = data.payload ?? {}
        handlers.onBookingComplete(p)
        dispatchCustomEvent('managepatients:bookingComplete', p)
        break
      }

      case 'mp:booking:error': {
        const p: BookingErrorPayload = data.payload ?? { message: 'Unknown error' }
        handlers.onBookingError(p)
        dispatchCustomEvent('managepatients:bookingError', p)
        break
      }

      case 'mp:close-request': {
        handlers.onCloseRequest()
        break
      }

      case 'mp:booking:redirect':
      case 'mp:checkout:redirect': {
        // Full-page redirect requested by the iframe — either the org has a
        // booking_success_url set, or a deposit flow needs to reach Stripe
        // Checkout (which sends frame-blocking headers, so the parent page
        // must navigate, not the iframe frame).
        const redirectUrl: string | undefined = (data.payload as { url?: string })?.url
        if (redirectUrl && typeof redirectUrl === 'string') {
          try { window.location.href = redirectUrl } catch { /* ignore */ }
        }
        break
      }

      case 'mp:height-hint': {
        const h = Number((data.payload as { height?: unknown })?.height)
        if (handlers.source && e.source !== handlers.source.contentWindow) break
        if (handlers.onHeightHint && Number.isFinite(h) && h > 0) handlers.onHeightHint(h)
        break
      }

      case 'mp:scroll-request':
        // Reserved — not yet acted on by the host
        break

      // ── Legacy v0 fallback (no version field) ─────────────────────────────
      case 'mp:booking-complete': {
        if (!isV1) {
          const p: BookingCompletePayload = data.payload ?? {}
          handlers.onBookingComplete(p)
          dispatchCustomEvent('managepatients:bookingComplete', p)
        }
        break
      }

      case 'mp:close': {
        if (!isV1) {
          handlers.onCloseRequest()
        }
        break
      }
    }
  }

  window.addEventListener('message', handleMessage)
  return () => window.removeEventListener('message', handleMessage)
}
